/**
 * Microcourse Studio — useTTS hook
 * TTS 播放控制：优先播放后端生成的 audioUrl，无 URL 时降级为浏览器 Web Speech API
 */

import { useRef, useState, useCallback, useEffect } from 'react'

export interface TTSState {
  /** 当前是否正在播放 */
  speaking: boolean
  /** 当前播放的 segment key (slideId:segIndex) */
  currentKey: string | null
}

export interface UseTTSReturn extends TTSState {
  /** 播放一段 TTS（有 audioUrl 用 Audio，否则用 speechSynthesis），返回 Promise 在播放结束后 resolve */
  playSegment: (slideId: string, segIndex: number, ttsText: string, audioUrl?: string) => Promise<void>
  /** 停止当前播放 */
  stop: () => void
  /** 暂停 */
  pause: () => void
  /** 恢复 */
  resume: () => void
}

export function useTTS(): UseTTSReturn {
  const [speaking, setSpeaking] = useState(false)
  const [currentKey, setCurrentKey] = useState<string | null>(null)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const speechDoneRef = useRef<(() => void) | null>(null)
  const stoppedRef = useRef(false)
  const pausedRef = useRef(false)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stoppedRef.current = true
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel()
      }
    }
  }, [])

  const stop = useCallback(() => {
    stoppedRef.current = true
    pausedRef.current = false
    setSpeaking(false)
    setCurrentKey(null)

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel()
    }
    if (speechDoneRef.current) {
      const done = speechDoneRef.current
      speechDoneRef.current = null
      done()
    }
  }, [])

  const pause = useCallback(() => {
    pausedRef.current = true
    if (audioRef.current) {
      audioRef.current.pause()
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.pause()
    }
  }, [])

  const resume = useCallback(() => {
    pausedRef.current = false
    if (audioRef.current) {
      audioRef.current.play().catch(() => {})
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.resume()
    }
  }, [])

  /** 播放音频 URL，结束后 resolve */
  const playAudioUrl = useCallback((url: string): Promise<void> => {
    return new Promise((resolve) => {
      const audio = new Audio(url)
      audioRef.current = audio
      let settled = false

      const done = () => {
        if (settled) return
        settled = true
        if (audioRef.current === audio) audioRef.current = null
        resolve()
      }

      audio.addEventListener('ended', done)
      audio.addEventListener('error', done)
      // 当 stop() 调用 pause 后也 resolve
      audio.addEventListener('pause', () => {
        if (stoppedRef.current) done()
      })

      audio.play().catch(done)
    })
  }, [])

  /** 浏览器 Web Speech API 朗读，结束后 resolve */
  const speakBrowser = useCallback((text: string): Promise<void> => {
    return new Promise((resolve) => {
      if (!('speechSynthesis' in window)) {
        resolve()
        return
      }
      window.speechSynthesis.cancel()
      const utter = new SpeechSynthesisUtterance(text)
      utter.lang = 'zh-CN'
      utter.rate = 1.0

      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        if (speechDoneRef.current === done) speechDoneRef.current = null
        resolve()
      }
      speechDoneRef.current = done
      utter.onend = done
      utter.onerror = done
      window.speechSynthesis.speak(utter)
    })
  }, [])

  const playSegment = useCallback(
    async (slideId: string, segIndex: number, ttsText: string, audioUrl?: string) => {
      if (!ttsText || !ttsText.trim()) return

      stoppedRef.current = false
      const key = `${slideId}:${segIndex}`
      setSpeaking(true)
      setCurrentKey(key)

      try {
        if (audioUrl) {
          await playAudioUrl(audioUrl)
        } else {
          await speakBrowser(ttsText)
        }
      } finally {
        // 只有当前 key 还在播放时才更新状态
        if (currentKey === key || !stoppedRef.current) {
          // 播放完毕自然结束
        }
      }

      // 自然结束后清除状态（如果不是被 stop 打断的）
      if (!stoppedRef.current) {
        setSpeaking(false)
        setCurrentKey(null)
      }
    },
    [playAudioUrl, speakBrowser, currentKey]
  )

  return {
    speaking,
    currentKey,
    playSegment,
    stop,
    pause,
    resume,
  }
}
